import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import {
  IconButton,
  Popover,
  PopoverProps,
  Typography
} from '@material-ui/core';
import {
  Add,
  AddCircle,
  Description,
  MoreVert,
  RemoveCircle
} from '@material-ui/icons';

import { useTraceBoardContext } from './TraceBoardContext';

const StyledDiv = styled.div`
  display: flex;
  white-space: nowrap;
  flex-wrap: nowrap;
  background: #fff;
  padding: 0 12px;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 1;
  .expand-collapse {
    padding: 12px 10px 12px 9px;
    .icon-button {
      .collapse {
        color: #fec400;
      }
      .expand {
        color: #29cc97;
      }
    }
  }
  .column-header {
    display: flex;
    flex: 1;
    min-width: 320px;
    align-items: center;
    padding: 12px 16px;
    border-right: 1px solid #dfe0eb;
    &:last-child {
      border-right: none;
    }
    .buttons {
      display: flex;
      flex: 1;
      justify-content: flex-end;
    }
    .title {
      margin-left: 6px;
    }
    .column-header-link {
      color: #000;
    }
  }
`;

export interface TraceBoardHeaderProps {}

const TraceBoardHeader: React.FC<TraceBoardHeaderProps> = () => {
  const { columns, rowCollapse, setColumns, setSelected, toggleCollapseRow } =
    useTraceBoardContext();
  // useEffect(() => {
  //   const isEmptyState = !columns.some(c => c.items.length > 0);
  //   if (isEmptyState) {
  //     setEmptyStateAnchor(primaryColumnButtonRef.current);
  //   }
  // }, [columns]);
  const [popoverProps, setPopoverProps] = useState<Partial<PopoverProps>>({});
  const [moreMenu, setMoreMenu] = useState<{
    anchorElement: HTMLButtonElement;
    columnIndex: number;
  }>();

  return (
    <StyledDiv className='board-header'>
      <div className='expand-collapse'>
        <IconButton
          size='small'
          className='icon-button'
          onClick={() => toggleCollapseRow()}
        >
          {rowCollapse.length === 0 || rowCollapse.some(r => !r) ? (
            <RemoveCircle className='collapse' />
          ) : (
            <AddCircle className='expand' />
          )}
        </IconButton>
      </div>
      {columns.map((column, index) => (
        <div className='column-header' key={index}>
          <Description />
          <Typography variant='overline' className='title'>
            {column.headerLink ? (
              <Link className='column-header-link' to={column.headerLink}>
                {column.title}
              </Link>
            ) : (
              column.title
            )}
          </Typography>
          <div className='buttons'>
            <IconButton
              size='small'
              disabled={
                column.items.some(i => i.id === 'new') || column.disableAdd
              }
              onClick={() => {
                const newItem = { id: 'new', description: '', data: {} };
                column.items.unshift(newItem);
                setColumns([...columns]);
                setSelected({ columnIndex: index, itemId: 'new' });
              }}
            >
              <Add />
            </IconButton>
            {column.renderColumnMenu && (
              <IconButton
                size='small'
                onClick={event =>
                  setMoreMenu({
                    anchorElement: event.currentTarget,
                    columnIndex: index
                  })
                }
              >
                <MoreVert />
              </IconButton>
            )}
          </div>
        </div>
      ))}
      {moreMenu &&
        (() => {
          const { columnIndex, anchorElement } = moreMenu;
          const column = columns[columnIndex];
          if (!column?.renderColumnMenu) return null;
          return (
            <Popover
              open
              anchorEl={anchorElement}
              onClose={() => setMoreMenu(undefined)}
              {...popoverProps}
            >
              {column.renderColumnMenu(
                { columnIndex, column },
                () => {
                  setMoreMenu(undefined);
                },
                () => {
                  // handle anchoring if column menu is dynamic / async loading
                  setPopoverProps({
                    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
                    transformOrigin: { vertical: 'top', horizontal: 'right' }
                  });
                }
              )}
            </Popover>
          );
        })()}
    </StyledDiv>
  );
};

export default TraceBoardHeader;
